import { useEffect, useState } from "react";
import { InfoModal, Button } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";
import { useStandardsModalContext } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { TOTAL_RESULTS_LIMIT_PER_PAGE } from "@/search/Langston/StandardsSearchProvider/StandardsSearchProvider.constants";
import { modalCss } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModal/StandardsModal.styles";
import { StandardsModalTabs } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModal/StandardsModalTabs";
import { StandardsConfirmAction } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModal/shared/StandardsConfirmAction";
import { LessonPlannerStandardsModalProps } from "./LessonPlannerStandardsModal.interface";

const ButtonWithTrackEvent = withTrackEvent(Button);

export const LessonPlannerStandardsModal = ({
  modalProps,
  setStandardsModalActive,
  setSelectedStandards,
}: LessonPlannerStandardsModalProps) => {
  const STANDARDS_LIMIT = 3;

  const {
    getters: { isConfirmActionModalOpen },
  } = useStandardsModalContext();

  const {
    fetchFilteredStandards,
    allStandardsModalResults,
    setSelectedStandards: setSearchSelectedStandards,
    getTemporarySelectedFilters,
    resetStandardsSearch,
    clearTemporarySelectedFilters,
  } = useStandardsSearchContext();
  const [hasFetchedStandards, setHasFetchedStandards] = useState(false);

  useEffect(() => {
    if (
      modalProps.isActive &&
      !hasFetchedStandards &&
      allStandardsModalResults.length !== TOTAL_RESULTS_LIMIT_PER_PAGE
    ) {
      fetchFilteredStandards();
      setHasFetchedStandards(true);
    }
  }, [modalProps.isActive, hasFetchedStandards, allStandardsModalResults]);

  useEffect(() => {
    if (modalProps.isActive) {
      clearTemporarySelectedFilters();
      setSearchSelectedStandards([]);
    }
  }, [modalProps.isActive, setSearchSelectedStandards]);

  const handleCancel = () => {
    resetStandardsSearch();
    clearTemporarySelectedFilters();
    setStandardsModalActive(false);
  };

  const handleApplyStandards = () => {
    const temporarySelectedStandards = getTemporarySelectedFilters();

    const uniqueStandards = temporarySelectedStandards.filter(
      (standard, index, self) =>
        index === self.findIndex((s) => s.id === standard.id),
    );

    setSelectedStandards(uniqueStandards);
    setStandardsModalActive(false);
  };

  const customFooter = (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginLeft: "auto",
        }}
      >
        <ButtonWithTrackEvent
          onClick={handleCancel}
          flavor={Button.flavors.secondary}
          objectType={objectTypes.content}
          actionName={actionNames.cancel}
          streamProperties={{
            description: "Cancel standards selection in lesson planner",
          }}
          dataProps={{
            "data-qa-selector": "lesson-planner-standards-modal-cancel",
          }}
          __cssFor={{
            root: {
              margin: "0 6px",
              padding: "16px",
            },
          }}
        >
          Cancel
        </ButtonWithTrackEvent>
        <ButtonWithTrackEvent
          onClick={handleApplyStandards}
          flavor={Button.flavors.primary}
          objectType={objectTypes.content}
          actionName={actionNames.create}
          streamProperties={{
            description: "Apply selected standards to lesson planner",
          }}
          dataProps={{
            "data-qa-selector": "lesson-planner-standards-modal-apply",
          }}
          __cssFor={{
            root: {
              margin: "0 6px",
              padding: "16px",
              marginRight: "12px",
            },
          }}
        >
          Add standards
        </ButtonWithTrackEvent>
      </div>
    </div>
  );

  return (
    <>
      <InfoModal
        __cssFor={modalCss(isConfirmActionModalOpen)}
        labelId="lesson-planner-standards-modal"
        title="Apply standards"
        {...modalProps}
        footer={customFooter}
      >
        <StandardsConfirmAction>
          <StandardsModalTabs
            selectionLimit={STANDARDS_LIMIT}
            canCreateOrEdit={false}
          />
        </StandardsConfirmAction>
      </InfoModal>
    </>
  );
};
